import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { useMutation } from '@apollo/client'
import { ChatGPT_Make, ChatGPT_Result } from '~/graphqls/gqls'
import {
  AiCreateArtworkModel,
  chatGptMake,
  chatGptMakeVariables,
  GPTVisibleState,
  chatGptResult,
  chatGptResultVariables,
  OptPromptResultStatus,
  Language,
} from '~/graphqls/types'
import { graphQLErrorMessage } from '~/utils'
import { useValidateIntergal } from '~/hooks/useValidateIntergal'
import Typewriter from 'typewriter-effect'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { useStores } from '~/hooks/useStores'
import CryptoJS from 'crypto-js'
import Cookies from 'js-cookie'
import { message } from 'antd'

export const ChatGPTContent: React.FC<{
  gptVisibleState: GPTVisibleState
  setGptVisibleState: Dispatch<SetStateAction<GPTVisibleState>>
  model: AiCreateArtworkModel
  keyText: string
  onResult: (text: string) => void
  isEnglish: boolean
}> = ({
  model,
  gptVisibleState,
  setGptVisibleState,
  keyText,
  isEnglish,
  onResult,
}) => {
  const { IsNotEnough, intergalModal } = useValidateIntergal()
  const { store } = useStores()
  // gpt描述文本
  const [gptContent, setGptContent] = useState('')
  //获取gpt结果
  let timerId: any
  const [taskId, setTaskId] = useState('')

  // 生成gpt描述词
  const [chatGptMakeAction] = useMutation<chatGptMake, chatGptMakeVariables>(
    ChatGPT_Make
  )

  // 异步获取生成gpt描述词
  const chatGptResultAction = useImperativeQuery<
    chatGptResult,
    chatGptResultVariables
  >(ChatGPT_Result)

  //页面显示是检测是否有gpt任务或gptVisibleState状态为loading
  useEffect(() => {
    const taskIdUid = Cookies.get('gptTaskId') ?? ''
    const taskId = taskIdUid.split('_')[0]

    if (gptVisibleState == GPTVisibleState.loading) {
      if (taskId.length > 0) {
        setTaskId(taskId)
      } else {
        gptMake()
      }
    }
  }, [gptVisibleState])

  //轮询获取gpt结果
  useEffect(() => {
    if (taskId.length > 0) {
      timerId = window.setInterval(() => {
        getGptResult(taskId, timerId)
      }, 3000)

      return () => {
        window.clearInterval(timerId)
      }
    }
  }, [taskId])

  //自动输入时滚动自动输入控件到底部
  useEffect(() => {
    if (gptContent.length > 0) {
      const scrollId = window.setInterval(() => {
        if (gptVisibleState == GPTVisibleState.auto) {
          let scrollItem = document.getElementById('autoScroll')
          if (scrollItem != null) {
            scrollItem!.scrollTop = scrollItem!.scrollHeight
          } else {
            window.clearInterval(scrollId)
          }
        } else {
          window.clearInterval(scrollId)
        }
      }, 100)

      return () => {
        window.clearInterval(scrollId)
      }
    }
  }, [gptContent])

  const removeTask = (timerId: any) => {
    window.clearInterval(timerId)
    Cookies.remove('gptTaskId')
  }

  // 生成chatgpt描述
  const gptMake = useCallback(() => {
    let randTaskId = CryptoJS.MD5(keyText + Date.now()).toString()
    chatGptMakeAction({
      variables: {
        model: model,
        text: keyText,
        taskId: randTaskId,
        language: isEnglish ? Language.ENGLISH : Language.CHINESE,
      },
    })
      .then(res => {
        let taskId = res.data?.chatGPTMutation?.content?.taskId ?? ''
        // let timeSeconds = res.data?.chatGPTMutation?.content?.timeSeconds ?? 1
        if (taskId.length > 0) {
          setTaskId(taskId)
          Cookies.set('gptTaskId', taskId + '_' + store.userinfo?.base?.uid, {
            expires: 1,
          })
        } else {
          message.destroy()
          message.info('生成描述失败')

          onErrorReset()
        }
      })
      .catch(err => {
        /** 积分不足 */
        if (IsNotEnough(err)) {
          intergalModal()
          setGptVisibleState(GPTVisibleState.idle)
        } else {
          message.destroy()
          message.error(graphQLErrorMessage(err) || '生成描述失败')
          onErrorReset()
        }
      })
  }, [])

  // 获取生成chatgpt描述
  const getGptResult = useCallback((taskId: string, timerId: any) => {
    chatGptResultAction({ taskId: taskId }).then(res => {
      let status =
        res.data?.chatGPTQuery?.gptResult?.status ??
        OptPromptResultStatus.PROCESSING
      let content = res.data?.chatGPTQuery?.gptResult?.result ?? ''
      if (status == OptPromptResultStatus.SUCCESS) {
        setGptContent(content)
        setGptVisibleState(GPTVisibleState.auto)
        removeTask(timerId)
      } else if (status == OptPromptResultStatus.FAIL) {
        message.destroy()
        message.error('生成描述失败')

        onErrorReset()
        removeTask(timerId)
      }
    })
  }, [])

  const onErrorReset = () => {
    setGptVisibleState(GPTVisibleState.error)
    setTimeout(() => {
      setGptVisibleState(GPTVisibleState.idle)
    }, 2000)
  }

  return (
    <>
      {gptVisibleState == GPTVisibleState.loading ||
      gptVisibleState == GPTVisibleState.error ? (
        <div className="w-full h-[148px] bg-[var(--neutral-200)] rounded-xl absolute z-30">
          <div className="flex flex-col h-full items-center justify-center">
            <img
              src={
                gptVisibleState == GPTVisibleState.loading
                  ? '/images/ai/chatgpt_bot.png'
                  : '/images/ai/chatgpt_error.svg'
              }
              alt=""
              className="w-10 h-10"
            ></img>
            {gptVisibleState == GPTVisibleState.loading ? (
              <div className="flex text-xs font-semibold w-full justify-center">
                <div className="text-[var(--primary)]">ChatGPT 生成中</div>
                <img
                  alt=""
                  src="/images/ai/chatgpt_loading_blue.png"
                  className="h-5"
                ></img>
              </div>
            ) : (
              <div className="text-[var(--error)]">生成描述失败</div>
            )}
          </div>
        </div>
      ) : gptVisibleState == GPTVisibleState.auto ? (
        <div className="w-full bg-[var(--neutral-100)] border-2 border-[var(--primary)] text-white text-xs rounded-xl absolute z-30">
          <div id="autoScroll" className="m-3 overflow-y-auto !h-[124px]">
            <Typewriter
              onInit={typewriter => {
                typewriter
                  .typeString(gptContent)
                  .callFunction(() => {
                    setGptVisibleState(GPTVisibleState.idle)
                    onResult(gptContent)
                  })
                  .start()
              }}
              options={{ delay: 1 }}
            />
          </div>
        </div>
      ) : null}
    </>
  )
}
